export type ExperimentStatus = 'draft' | 'running' | 'stopped';
export type ExperimentField = 'name' | 'notes' | 'startsAt' | 'endsAt' | 'variants';
export interface ExperimentVariant {
  key: string;
  weight: number;
}

/**
 * Copied from the backend `src/modules/experiments/experiment-bucketing.ts`:
 * `EXPERIMENT_KEY_PATTERN`, `VARIANT_KEY_PATTERN`, `CONTROL_VARIANT`, `MAX_RUNNING_EXPERIMENTS`.
 * Keep them in step with that file. The GA4 user property is exp_<key>, which must fit in 24
 * characters.
 */
export const EXPERIMENT_KEY_PATTERN = /^[a-z][a-z0-9_]{0,19}$/;
const VARIANT_KEY_PATTERN = /^[a-z0-9_]{1,20}$/;
const CONTROL_VARIANT = 'control';
export const MAX_RUNNING_EXPERIMENTS = 5;

export function defaultVariants(): ExperimentVariant[] {
  return [
    { key: CONTROL_VARIANT, weight: 50 },
    { key: 'b', weight: 50 },
  ];
}

/**
 * Mirror of the backend `validateVariants` (experiment-bucketing.ts), same messages.
 * [] when valid; otherwise every problem found.
 */
export function variantProblems(variants: unknown): string[] {
  const shapeError = 'variants must be an array of 2 to 5 { key, weight }';
  if (!Array.isArray(variants)) return [shapeError];

  const errors: string[] = [];
  if (variants.length < 2 || variants.length > 5) errors.push(shapeError);

  const seen = new Set<string>();
  let sum = 0;
  let hasControl = false;
  for (const item of variants) {
    if (item === null || typeof item !== 'object') {
      if (!errors.includes(shapeError)) errors.push(shapeError);
      continue;
    }
    const { key, weight } = item as { key?: unknown; weight?: unknown };
    const label = String(key);
    if (typeof key !== 'string' || !VARIANT_KEY_PATTERN.test(key)) {
      errors.push(`variant key "${label}" must match ^[a-z0-9_]{1,20}$`);
    } else if (seen.has(key)) {
      errors.push(`duplicate variant key "${key}"`);
    } else {
      seen.add(key);
    }
    if (key === CONTROL_VARIANT) hasControl = true;
    if (typeof weight !== 'number' || !Number.isInteger(weight) || weight < 1 || weight > 99) {
      errors.push(`weight of "${label}" must be an integer from 1 to 99`);
    }
    if (typeof weight === 'number' && Number.isFinite(weight)) sum += weight;
  }

  if (sum !== 100) errors.push(`weights must sum to 100 (got ${sum})`);
  if (!hasControl) errors.push('a variant named exactly "control" is required');
  return errors;
}

/** Fields the Edit form may change per status (the key never changes). */
export function editableFields(status: ExperimentStatus): ExperimentField[] {
  switch (status) {
    case 'draft':
      return ['name', 'notes', 'startsAt', 'endsAt', 'variants'];
    case 'running':
      return ['name', 'notes', 'endsAt'];
    default:
      return [];
  }
}

/** Live sum of the weights in the form; non-numbers count as 0. */
export function weightSum(variants: unknown): number {
  if (!Array.isArray(variants)) return 0;
  return variants.reduce((sum: number, v) => {
    const w = (v as { weight?: unknown } | null)?.weight;
    return sum + (typeof w === 'number' && Number.isFinite(w) ? w : 0);
  }, 0);
}

/** `control 50 · on 50` */
export function formatVariants(variants: ExperimentVariant[] | undefined): string {
  return (variants ?? []).map((v) => `${v.key} ${v.weight}`).join(' · ');
}
